"use client";

import * as React from "react";
import { Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-4 rounded-xl border border-border bg-accent/30 p-8 text-center">
        <div className="flex size-12 items-center justify-center rounded-lg bg-primary/10">
          <svg className="size-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="text-lg font-default font-medium text-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-lg border-ring text-ring hover:bg-ring/10">
          Send Another Message
        </Button>
      </div>;
  }
  const inputClasses = "flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm font-sans";
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Your Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground">Your Name</FormLabel>
              <FormControl>
                <input type="text" className={inputClasses} placeholder="Julian Smith" {...field} />
              </FormControl>
              <FormDescription className="text-muted-foreground font-sans">Enter your full name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground">Email Address</FormLabel>
              <FormControl>
                <input type="email" className={inputClasses} placeholder="julian@example.com" {...field} />
              </FormControl>
              <FormDescription className="text-muted-foreground font-sans">We'll use this to reply to your inquiry.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-foreground">Phone Number</FormLabel>
            <FormControl>
              <input type="tel" className={inputClasses} placeholder="(860) 835-5222" {...field} />
            </FormControl>
            <FormDescription className="text-muted-foreground font-sans">Optional – for quick follow-ups.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-foreground">Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your catering needs or market questions..." className="min-h-[120px] resize-y rounded-md border-input bg-transparent shadow-sm focus-visible:ring-ring/50 font-sans" {...field} />
            </FormControl>
            <FormDescription className="text-muted-foreground font-sans">Share your thoughts or specific requests.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive font-sans">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto rounded-lg bg-ring text-white hover:bg-ring/90 transition-all shadow-md" size="lg">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
}
export default function SayHello() {
  return <section data-section-id="236241" id="say-hello" className="bg-background text-foreground py-24 lg:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
          {}
          <div className="flex flex-col justify-center space-y-10">
            <div className="space-y-4">
              <h2 className="font-serif text-5xl font-medium tracking-tight text-foreground lg:text-7xl">
                Say Hello
              </h2>
              <p className="max-w-md text-lg leading-relaxed text-muted-foreground font-sans">
                Whether you're craving a fresh focaccia sandwich, looking for imported European treats, or want to chat about catering, we'd love to hear from you.
              </p>
            </div>

            <div className="space-y-8">
              <div>
                <p className="mb-2 text-sm font-default font-semibold uppercase tracking-[0.1em] text-secondary">
                  Call the Café
                </p>
                <Link to="tel:+18608355222" className="font-serif text-3xl font-medium text-foreground transition-colors hover:text-ring lg:text-4xl">
                  (860) 835-5222
                </Link>
              </div>

              <div>
                <p className="mb-2 text-sm font-default font-semibold uppercase tracking-[0.1em] text-secondary">
                  Find Us in Enfield
                </p>
                <address className="text-lg not-italic text-foreground font-sans leading-relaxed">
                  786 Enfield St, Suite 1<br />
                  Enfield, CT 06082
                </address>
              </div>
              
              <div>
                <p className="mb-2 text-sm font-default font-semibold uppercase tracking-[0.1em] text-secondary">
                  Café Hours
                </p>
                <p className="text-lg text-foreground font-sans">
                  Tue–Sat: 7:30 am – 6 pm<br />
                  Sun: 8 am – 4 pm
                </p>
              </div>
            </div>
          </div>

          {}
          <div className="rounded-2xl border border-border bg-card p-6 shadow-lg sm:p-8 lg:p-10">
            <div className="mb-8">
              <h3 className="font-serif text-2xl font-medium text-foreground">
                Drop Us a Line
              </h3>
              <p className="mt-2 text-sm text-muted-foreground font-sans">
                Fill out the form below and our team will get back to you as soon as we've finished the morning rush.
              </p>
            </div>

            <FormProvider formKey="say_hello_form_visit_us" formId="say_hello_form_visit_us" sectionName="say-hello" defaultValues={{
            name: "",
            email: "",
            phone: "",
            message: ""
          }} submitText="Send Message" successMessage="Thank you for reaching out! We'll be in touch soon with a warm welcome.">
              <ContactForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}